<script setup>
import { computed, ref } from 'vue'
import { useI18n } from '../../i18n'

const props = defineProps({
  stations: { type: Array, required: true },
  modelValue: { type: Array, required: true },
  sourceModelValue: { type: Array, default: () => [] },
  pts2ModelValue: { type: Array, default: () => [] },
  pts2Enabled: { type: Boolean, default: false },
  all: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'update:sourceModelValue', 'update:pts2ModelValue'])
const search = ref('')
const { t } = useI18n()
const filtered = computed(() => props.stations.filter((station) => station.displayName.toLowerCase().includes(search.value.toLowerCase())))

function toggle(stationId) {
  const next = new Set(props.modelValue)
  if (next.has(stationId)) {
    next.delete(stationId)
    const station = props.stations.find((item) => item.id === stationId)
    const sourceIds = new Set((station?.naftaPosInstances ?? []).map((source) => source.id))
    const controllerIds = new Set((station?.pts2Controllers ?? []).map((controller) => controller.id))
    emit('update:sourceModelValue', props.sourceModelValue.filter((id) => !sourceIds.has(id)))
    emit('update:pts2ModelValue', props.pts2ModelValue.filter((id) => !controllerIds.has(id)))
  } else next.add(stationId)
  emit('update:modelValue', [...next])
}

function togglePts2(station, controllerId) {
  if (!props.pts2Enabled) return
  const controllers = new Set(props.pts2ModelValue)
  if (controllers.has(controllerId)) controllers.delete(controllerId)
  else controllers.add(controllerId)
  emit('update:pts2ModelValue', [...controllers])
  if (!props.modelValue.includes(station.id)) emit('update:modelValue', [...props.modelValue, station.id])
}

function toggleSource(station, sourceId) {
  const sources = new Set(props.sourceModelValue)
  if (sources.has(sourceId)) sources.delete(sourceId)
  else sources.add(sourceId)
  emit('update:sourceModelValue', [...sources])
  if (!props.modelValue.includes(station.id)) emit('update:modelValue', [...props.modelValue, station.id])
}

function selectAll() {
  emit('update:modelValue', filtered.value.map((station) => station.id))
  emit('update:sourceModelValue', filtered.value.flatMap((station) => station.naftaPosInstances ?? []).map((source) => source.id))
  emit('update:pts2ModelValue', props.pts2Enabled
    ? filtered.value.flatMap((station) => station.pts2Controllers ?? []).map((controller) => controller.id)
    : [])
}
</script>

<template>
  <div v-if="all" class="station-all-notice"><strong>{{ t('allStationsAutomatic') }}</strong><p>{{ t('allStationsAutomaticHint') }}</p></div>
  <div v-else class="station-scope-step">
    <div class="station-scope-tools"><input v-model="search" type="search" :placeholder="t('searchStation')" /><span>{{ t('selectedStations', { selected: modelValue.length, total: stations.length }) }}</span><button class="text-action" type="button" @click="selectAll">{{ t('selectAllStations') }}</button></div>
    <p class="notice warning">{{ t('stationScopeWarning') }}</p>
    <div class="station-check-list">
      <div v-for="station in filtered" :key="station.id" class="station-scope-group" :class="{ selected: modelValue.includes(station.id) }">
        <label class="station-scope-parent">
          <input type="checkbox" :checked="modelValue.includes(station.id)" @change="toggle(station.id)" />
          <span><strong>{{ station.displayName }}</strong><small>{{ station.stableCode || t('stationNumber', { id: station.id }) }}</small></span>
          <em>{{ station.status === 'ACTIVE' || station.enabled ? t('active') : t('disabled') }}</em>
        </label>
        <div v-if="modelValue.includes(station.id)" class="station-source-scope">
          <strong class="station-scope-service-label">NaftaPOS</strong>
          <label v-for="source in station.naftaPosInstances ?? []" :key="source.id">
            <input type="checkbox" :checked="sourceModelValue.includes(source.id)" @change="toggleSource(station, source.id)" />
            <span><strong>{{ source.displayName }}</strong><small>{{ t('naftaPosWorkplace') }}</small></span>
          </label>
          <p v-if="!(station.naftaPosInstances ?? []).length">{{ t('noNaftaPosWorkplaces') }}</p>
          <strong class="station-scope-service-label">PTS-2</strong>
          <label v-for="controller in station.pts2Controllers ?? []" :key="`pts2-${controller.id}`" :class="{ disabled: !pts2Enabled }">
            <input type="checkbox" :checked="pts2ModelValue.includes(controller.id)" :disabled="!pts2Enabled" @change="togglePts2(station, controller.id)" />
            <span><strong>{{ controller.displayName }}</strong><small>{{ t('pts2ControllerScope') }} · {{ controller.stableCode }}</small></span>
          </label>
          <p v-if="!pts2Enabled">{{ t('pts2RolePermissionRequired') }}</p>
          <p v-else-if="!(station.pts2Controllers ?? []).length">{{ t('pts2NoControllers') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
